/* eslint-disable arrow-body-style */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import MediaUploadsService from '../../services/MediaUploadsService';

export const mediaUploadsKeys = {
  all: ['mediaUploads'],
  verifications: () => [...mediaUploadsKeys.all, 'verifications'],
  verification: (modelId) => [...mediaUploadsKeys.verifications(), modelId],
  medias: () => [...mediaUploadsKeys.all, 'medias'],
  mediasPaginated: (modelId, type) => [
    ...mediaUploadsKeys.medias(),
    modelId,
    type,
  ],
};

/**
 * Hook para buscar verificação de mídia da model
 * @param {string} modelId - ID da model
 */
export function useModelVerification(modelId) {
  return useQuery({
    queryKey: mediaUploadsKeys.verification(modelId),
    queryFn: () => MediaUploadsService.listVerification(modelId),
    enabled: !!modelId,
    staleTime: 10 * 60 * 1000, // 5 minutos
    gcTime: 30 * 60 * 1000, // 10 minutos no cache
    retry: 2,
  });
}

export function useGetPresignedUrl() {
  return useMutation({
    mutationFn: ({ file, label }) =>
      MediaUploadsService.getPresignedVerification(file, label),
  });
}

export function useInvalidateVerification() {
  const queryClient = useQueryClient();

  return (modelId) => {
    queryClient.invalidateQueries({
      queryKey: mediaUploadsKeys.verification(modelId),
    });
  };
}

/**
 * Hook para buscar mídias da model com paginação infinita
 * @param {string} modelId - ID da model
 * @param {string|null} type - Tipo de mídia ('photo' ou 'video')
 */
export function useModelMediasInfinite(modelId, type = null) {
  return useInfiniteQuery({
    queryKey: mediaUploadsKeys.mediasPaginated(modelId, type),
    queryFn: async ({ pageParam = 1 }) => {
      const result = await MediaUploadsService.listMedias(
        modelId,
        type,
        pageParam
      );

      return {
        urls: result.urls,
        pagination: result.pagination,
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasMore
        ? lastPage.pagination.page + 1
        : undefined;
    },
    initialPageParam: 1,
    enabled: !!modelId,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 15 * 60 * 1000,
    retry: 2,
  });
}
